import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import AccountDropdown from './AccountDropdown';
import ChatPanel from './ChatPanel';
import { apiFetch } from './api';
import './VBPPage.css';

// ── Shared form helpers ───────────────────────────────────────────────────────

function Field({ label, hint, children }) {
  return (
    <div className="mb-3">
      <label className="form-label fw-semibold">{label}</label>
      {children}
      {hint && <div className="form-text" style={{ fontSize: '0.8rem' }}>{hint}</div>}
    </div>
  );
}

function CheckGroup({ options, value, onChange }) {
  return (
    <div className="d-flex flex-wrap gap-3">
      {options.map(opt => (
        <div key={opt} className="form-check mb-0">
          <input
            className="form-check-input"
            type="checkbox"
            id={`chk-${opt}`}
            checked={value.includes(opt)}
            onChange={e =>
              onChange(e.target.checked ? [...value, opt] : value.filter(v => v !== opt))
            }
          />
          <label className="form-check-label" htmlFor={`chk-${opt}`}>{opt}</label>
        </div>
      ))}
    </div>
  );
}

function SectionHeading({ children }) {
  return (
    <h6 className="vbp-section-heading">{children}</h6>
  );
}

// ── Bundle procedure options ─────────────────────────────────────────────────

const BUNDLE_PROCEDURES = {
  'Surgical Procedure': [
    'Hip Replacement (MS-DRG 470)',
    'Knee Replacement (MS-DRG 469/470)',
    'CABG (MS-DRG 231\u2013236)',
    'PCI (MS-DRG 246\u2013249)',
    'Lumbar Spinal Fusion (MS-DRG 460\u2013462)',
    'Colectomy (MS-DRG 329\u2013331)',
  ],
  'Medical Episode': [
    'CHF Exacerbation (MS-DRG 291\u2013293)',
    'COPD Exacerbation (MS-DRG 190\u2013192)',
    'Pneumonia (MS-DRG 177\u2013179)',
    'Sepsis (MS-DRG 870\u2013872)',
  ],
  'Maternity': [
    'Vaginal Delivery (MS-DRG 775\u2013776)',
    'C-Section Delivery (MS-DRG 783\u2013785)',
    'Global Maternity (prenatal + delivery + postpartum)',
  ],
};

// ── Program type registry ─────────────────────────────────────────────────────

const PROGRAM_TYPES = [
  'Total Cost of Care',
  'Specialty Capitation / Sub-Capitation',
  'Condition Specific PMPM',
  'Bundling',
];

const TYPE_DEFAULTS = {
  'Total Cost of Care': {
    attribution: 'Claims-Based',
    benchmark: 'Regional Reference',
    period: 'Calendar Year',
    riskArrangement: 'One-Sided',
    lobs: ['Medicare Advantage'],
  },
  'Specialty Capitation / Sub-Capitation': {
    capitationType: 'Sub-Capitation',
    specialties: ['Cardiology'],
    carveOuts: [],
    network: 'Preferred',
    lobs: ['Medicare Advantage'],
  },
  'Condition Specific PMPM': {
    condition: 'Diabetes',
    lookback: '12 Months',
    minEnrollment: '6 Months',
    qualityMeasures: [],
    comorbidityAdj: false,
    lobs: ['Medicare Advantage'],
  },
  'Bundling': {
    bundleType: 'Surgical Procedure',
    procedure: BUNDLE_PROCEDURES['Surgical Procedure'][0],
    anchorEvent: 'Inpatient Admission',
    preTriggerDays: '3',
    postTriggerDays: '90',
    pricingMethod: 'Historical Benchmark',
    includedServices: ['Inpatient Facility', 'Professional Services'],
  },
};

// ── Type-specific field sections (controlled, no Program Identity) ─────────────

function TcocFields({ form, setForm }) {
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  return (
    <>
      <SectionHeading>Attribution &amp; Benchmarking</SectionHeading>
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <Field label="Attribution Method">
            <select className="form-select" value={form.attribution} onChange={set('attribution')}>
              {['Claims-Based', 'Enrollment-Based', 'Hybrid'].map(a => <option key={a}>{a}</option>)}
            </select>
          </Field>
        </div>
        <div className="col-md-4">
          <Field label="Benchmark Methodology">
            <select className="form-select" value={form.benchmark} onChange={set('benchmark')}>
              {['Regional Reference', 'National Reference', 'Historical Trend', 'Custom'].map(b =>
                <option key={b}>{b}</option>)}
            </select>
          </Field>
        </div>
        <div className="col-md-4">
          <Field label="Performance Period">
            <select className="form-select" value={form.period} onChange={set('period')}>
              {['Calendar Year', 'Rolling 12-Month', 'Custom'].map(p => <option key={p}>{p}</option>)}
            </select>
          </Field>
        </div>
      </div>

      <SectionHeading>Risk Structure</SectionHeading>
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <Field label="Risk Arrangement">
            <select className="form-select" value={form.riskArrangement} onChange={set('riskArrangement')}>
              <option>One-Sided</option>
              <option>Two-Sided</option>
            </select>
          </Field>
        </div>
      </div>

      <SectionHeading>Lines of Business</SectionHeading>
      <Field label="">
        <CheckGroup
          options={['Medicare Advantage', 'Medicaid Managed Care', 'Commercial / Exchange', 'CHIP', 'Federal Employee Program']}
          value={form.lobs}
          onChange={v => setForm(f => ({ ...f, lobs: v }))}
        />
      </Field>
    </>
  );
}

function CapitationFields({ form, setForm }) {
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  return (
    <>
      <SectionHeading>Capitation Structure</SectionHeading>
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <Field label="Capitation Type">
            <select className="form-select" value={form.capitationType} onChange={set('capitationType')}>
              <option>Full Capitation</option>
              <option>Sub-Capitation</option>
            </select>
          </Field>
        </div>
        <div className="col-md-4">
          <Field label="Network Requirement">
            <select className="form-select" value={form.network} onChange={set('network')}>
              <option>Exclusive</option>
              <option>Preferred</option>
              <option>Open</option>
            </select>
          </Field>
        </div>
      </div>

      <Field label="Covered Specialties">
        <CheckGroup
          options={['Cardiology', 'Orthopedics', 'Oncology', 'Neurology', 'Behavioral Health', 'OB/GYN', 'Radiology', 'Gastroenterology']}
          value={form.specialties}
          onChange={v => setForm(f => ({ ...f, specialties: v }))}
        />
      </Field>

      <SectionHeading>Carve-out Services</SectionHeading>
      <Field label="" hint="Services excluded from the capitation arrangement">
        <CheckGroup
          options={['Emergency / ED', 'Inpatient Facility', 'Laboratory', 'Radiology', 'Transplant', 'Behavioral Health', 'Pharmacy']}
          value={form.carveOuts}
          onChange={v => setForm(f => ({ ...f, carveOuts: v }))}
        />
      </Field>

      <SectionHeading>Lines of Business</SectionHeading>
      <Field label="">
        <CheckGroup
          options={['Medicare Advantage', 'Medicaid Managed Care', 'Commercial / Exchange', 'CHIP', 'Federal Employee Program']}
          value={form.lobs}
          onChange={v => setForm(f => ({ ...f, lobs: v }))}
        />
      </Field>
    </>
  );
}

function PmpmFields({ form, setForm }) {
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  return (
    <>
      <SectionHeading>Condition &amp; Attribution</SectionHeading>
      <div className="row g-3 mb-3">
        <div className="col-md-6">
          <Field label="Target Condition">
            <select className="form-select" value={form.condition} onChange={set('condition')}>
              {['Diabetes', 'Congestive Heart Failure', 'COPD', 'Coronary Artery Disease',
                'Chronic Kidney Disease', 'Hypertension', 'Obesity', 'Asthma', 'Atrial Fibrillation',
                'Major Depressive Disorder'].map(c => <option key={c}>{c}</option>)}
            </select>
          </Field>
        </div>
        <div className="col-md-6">
          <Field label="Attribution Lookback Window">
            <select className="form-select" value={form.lookback} onChange={set('lookback')}>
              {['6 Months', '12 Months', '18 Months', '24 Months'].map(l => <option key={l}>{l}</option>)}
            </select>
          </Field>
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <Field label="Minimum Enrollment Period" hint="Continuous enrollment required for attribution">
            <select className="form-select" value={form.minEnrollment} onChange={set('minEnrollment')}>
              {['1 Month', '3 Months', '6 Months', '12 Months'].map(m => <option key={m}>{m}</option>)}
            </select>
          </Field>
        </div>
      </div>

      <SectionHeading>Quality Measures</SectionHeading>
      <Field label="" hint="Select the quality measures that gate payment or apply a withhold">
        <CheckGroup
          options={['HbA1c Control (<8%)', 'Blood Pressure Control (<140/90)', 'LDL Control (<100 mg/dL)',
            'Medication Adherence (≥80%)', 'ED Utilization Rate', '30-Day Readmission Rate',
            'Annual Wellness Visit Completion', 'Specialist Referral Compliance']}
          value={form.qualityMeasures}
          onChange={v => setForm(f => ({ ...f, qualityMeasures: v }))}
        />
      </Field>

      <SectionHeading>Risk Adjustment</SectionHeading>
      <Field label="">
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            id="comorbSwitch"
            checked={form.comorbidityAdj}
            onChange={e => setForm(f => ({ ...f, comorbidityAdj: e.target.checked }))}
          />
          <label className="form-check-label" htmlFor="comorbSwitch">
            Apply risk-adjusted PMPM multiplier for documented comorbidities
          </label>
        </div>
      </Field>

      <SectionHeading>Lines of Business</SectionHeading>
      <Field label="">
        <CheckGroup
          options={['Medicare Advantage', 'Medicaid Managed Care', 'Commercial / Exchange', 'CHIP', 'Federal Employee Program']}
          value={form.lobs}
          onChange={v => setForm(f => ({ ...f, lobs: v }))}
        />
      </Field>
    </>
  );
}

function BundlingFields({ form, setForm }) {
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const changeBundleType = e => {
    const bt = e.target.value;
    setForm(f => ({ ...f, bundleType: bt, procedure: BUNDLE_PROCEDURES[bt][0] }));
  };
  return (
    <>
      <SectionHeading>Bundle Definition</SectionHeading>
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <Field label="Bundle Type">
            <select className="form-select" value={form.bundleType} onChange={changeBundleType}>
              <option>Surgical Procedure</option>
              <option>Medical Episode</option>
              <option>Maternity</option>
            </select>
          </Field>
        </div>
        <div className="col-md-8">
          <Field label="Procedure / Episode">
            <select className="form-select" value={form.procedure} onChange={set('procedure')}>
              {BUNDLE_PROCEDURES[form.bundleType].map(p => <option key={p}>{p}</option>)}
            </select>
          </Field>
        </div>
      </div>
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <Field label="Anchor Event">
            <select className="form-select" value={form.anchorEvent} onChange={set('anchorEvent')}>
              <option>Inpatient Admission</option>
              <option>Outpatient Procedure</option>
              <option>ED Visit</option>
            </select>
          </Field>
        </div>
        <div className="col-md-4">
          <Field label="Pre-Trigger Window (days)" hint="Days before anchor included in bundle">
            <input type="number" className="form-control" min="0" max="90"
              value={form.preTriggerDays} onChange={set('preTriggerDays')} />
          </Field>
        </div>
        <div className="col-md-4">
          <Field label="Post-Trigger Window (days)" hint="Days after anchor included in bundle">
            <input type="number" className="form-control" min="0" max="365"
              value={form.postTriggerDays} onChange={set('postTriggerDays')} />
          </Field>
        </div>
      </div>

      <SectionHeading>Pricing Methodology</SectionHeading>
      <div className="row g-3 mb-3">
        <div className="col-md-6">
          <Field label="Benchmark Pricing Method">
            <select className="form-select" value={form.pricingMethod} onChange={set('pricingMethod')}>
              <option>Historical Benchmark</option>
              <option>Regional Reference Price</option>
              <option>National CMS Reference</option>
              <option>Custom Negotiated Price</option>
            </select>
          </Field>
        </div>
      </div>

      <SectionHeading>Included Services</SectionHeading>
      <Field label="" hint="Service categories included within the bundle payment">
        <CheckGroup
          options={['Inpatient Facility', 'Professional Services', 'Outpatient Facility',
            'Post-Acute Care', 'Home Health', 'Skilled Nursing Facility',
            'Readmissions (30-day)', 'Durable Medical Equipment']}
          value={form.includedServices}
          onChange={v => setForm(f => ({ ...f, includedServices: v }))}
        />
      </Field>
    </>
  );
}

const TYPE_FIELDS = {
  'Total Cost of Care': TcocFields,
  'Specialty Capitation / Sub-Capitation': CapitationFields,
  'Condition Specific PMPM': PmpmFields,
  'Bundling': BundlingFields,
};

// ── Page shell ────────────────────────────────────────────────────────────────

export default function VBPPage({ onNavigate, currentUser, onLogout, chatStateRef }) {
  const [programs, setPrograms] = useState([]);
  const programsRef = useRef([]);
  useEffect(() => { programsRef.current = programs; }, [programs]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [editingId, setEditingId] = useState(null); // null=list, 'new'=creating, else id
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  // Hierarchy type (global setting)
  const [hierarchyType, setHierarchyType] = useState('Winner takes all');

  // Drag-to-reorder
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  // AI panel
  const aiPanelRef = useRef(null);
  const [aiCollapsed, setAiCollapsed] = useState(false);
  const toggleAi = useCallback(() => {
    if (aiCollapsed) {
      aiPanelRef.current?.expand();
    } else {
      aiPanelRef.current?.collapse();
    }
  }, [aiCollapsed]);

  const loadPrograms = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiFetch('/api/vbp/programs');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      // Merge typeData fields into the top-level object for the form
      setPrograms(data.map(p => ({ ...p, ...p.typeData })));
    } catch (e) {
      setApiError('Failed to load programs. ' + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPrograms(); }, [loadPrograms]);

  // Open the correct item from URL on first load
  const initializedFromURL = useRef(false);
  useEffect(() => {
    if (loading || initializedFromURL.current) return;
    initializedFromURL.current = true;
    const id = new URLSearchParams(window.location.search).get('id');
    if (id && id !== 'new') {
      const p = programsRef.current.find(x => x.id === id);
      if (p) { setDraft({ ...p }); setEditingId(p.id); }
      else history.replaceState(null, '', '/vbp');
    }
  }, [loading]);

  // Handle browser back / forward
  useEffect(() => {
    const handler = () => {
      const id = new URLSearchParams(window.location.search).get('id');
      if (!id) {
        setEditingId(null); setDraft(null); setDeleteConfirm(false); setApiError(null);
      } else if (id === 'new') {
        history.replaceState(null, '', '/vbp');
        setEditingId(null); setDraft(null);
      } else {
        const p = programsRef.current.find(x => x.id === id);
        if (p) { setDraft({ ...p }); setEditingId(p.id); setSaved(false); setApiError(null); setDeleteConfirm(false); }
        else { history.replaceState(null, '', '/vbp'); setEditingId(null); setDraft(null); }
      }
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  useEffect(() => {
    apiFetch('/api/settings')
      .then(r => r.json())
      .then(d => setHierarchyType(d.hierarchyType))
      .catch(() => {});
  }, []);

  const handleHierarchyChange = async (value) => {
    setHierarchyType(value);
    try {
      await apiFetch('/api/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hierarchyType: value }),
      });
    } catch (_) { /* silent */ }
  };

  const handleReorder = async (dropIndex) => {
    if (dragIdx === null || dragIdx === dropIndex) return;
    const reordered = [...programs];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(dropIndex, 0, moved);
    setPrograms(reordered);
    try {
      await apiFetch('/api/vbp/programs/reorder', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: reordered.map(p => p.id) }),
      });
    } catch (_) {
      await loadPrograms(); // revert on error
    }
  };

  const startNew = () => {
    history.pushState(null, '', '/vbp?id=new');
    setDraft({ name: '', contractYear: '2026', programType: '' });
    setEditingId('new');
    setSaved(false);
    setApiError(null);
    setDeleteConfirm(false);
  };

  const startEdit = prog => {
    history.pushState(null, '', '/vbp?id=' + prog.id);
    setDraft({ ...prog });
    setEditingId(prog.id);
    setSaved(false);
    setApiError(null);
    setDeleteConfirm(false);
  };

  const cancel = () => {
    history.pushState(null, '', '/vbp');
    setEditingId(null);
    setDraft(null);
    setDeleteConfirm(false);
    setApiError(null);
  };

  const handleTypeChange = type => {
    setDraft(d => ({ name: d.name, contractYear: d.contractYear, programType: type, ...TYPE_DEFAULTS[type] }));
  };

  const buildPayload = () => {
    const { name, contractYear, programType, id, ...typeData } = draft;
    return { name, contractYear, programType, typeData };
  };

  const handleSave = async e => {
    e.preventDefault();
    setSaving(true);
    setApiError(null);
    try {
      let res;
      if (editingId === 'new') {
        res = await apiFetch('/api/vbp/programs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(buildPayload()),
        });
      } else {
        res = await apiFetch(`/api/vbp/programs/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(buildPayload()),
        });
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const saved_prog = await res.json();
      const newId = saved_prog.id;
      if (editingId === 'new') {
        history.replaceState(null, '', '/vbp?id=' + newId);
        setEditingId(newId);
      }
      await loadPrograms();
      setSaved(true);
      setTimeout(() => setSaved(false), 3500);
    } catch (e) {
      setApiError('Failed to save: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      const res = await apiFetch(`/api/vbp/programs/${editingId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await loadPrograms();
      history.pushState(null, '', '/vbp');
      cancel();
    } catch (e) {
      setApiError('Failed to delete: ' + e.message);
    }
  };

  const TypeFields = draft?.programType ? TYPE_FIELDS[draft.programType] : null;

  return (
    <div className="d-flex flex-column" style={{ height: '100vh', background: 'var(--eh-navy)' }}>
      <nav className="navbar navbar-dark bg-primary px-4 py-2 flex-shrink-0">
        <span
          className="navbar-brand mb-0 d-flex align-items-center gap-3"
          style={{ cursor: 'pointer' }}
          onClick={() => onNavigate('home')}
        >
          <img src="/enlace-logo-white.svg" alt="Enlace Health" style={{ height: 36 }} />
          <span style={{ fontWeight: 300, opacity: 0.65, fontSize: '0.8rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#fff' }}>
            Value-Based Programs
          </span>
        </span>
        {currentUser && (
          <AccountDropdown currentUser={currentUser} onLogout={onLogout} />
        )}
      </nav>

      <div className="d-flex flex-grow-1 overflow-hidden">
        {/* ── Sidebar ── */}
        <div className="vbp-sidebar">
          <div className="vbp-sidebar-title">Value-Based Programs</div>

          {/* Hierarchy type global setting */}
          <div className="vbp-hierarchy-section">
            <div className="vbp-hierarchy-label">Hierarchy Type</div>
            <select
              className="vbp-hierarchy-select"
              value={hierarchyType}
              onChange={e => handleHierarchyChange(e.target.value)}
            >
              <option>Winner takes all</option>
              <option>Carve out</option>
              <option>Waterfall</option>
            </select>
          </div>

          {programs.map((p, idx) => (
            <button
              key={p.id}
              draggable
              className={`vbp-program-btn${editingId === p.id ? ' active' : ''}${dragOverIdx === idx && dragIdx !== idx ? ' drag-over' : ''}`}
              onClick={() => startEdit(p)}
              onDragStart={e => { setDragIdx(idx); e.dataTransfer.effectAllowed = 'move'; }}
              onDragOver={e => { e.preventDefault(); setDragOverIdx(idx); }}
              onDrop={e => { e.preventDefault(); handleReorder(idx); }}
              onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
            >
              <span className="vbp-drag-handle" title="Drag to reorder">⠿</span>
              <span className="vbp-program-btn-inner">
                <span className="vbp-program-name">{p.name || <em style={{ opacity: 0.6 }}>Untitled</em>}</span>
                <span className="vbp-program-type">{p.programType}</span>
              </span>
            </button>
          ))}
          <button className="vbp-add-btn" onClick={startNew}>+ Add Program</button>
        </div>

        {/* ── Main area + AI panel ── */}
        <PanelGroup direction="horizontal" style={{ flex: 1, minWidth: 0, background: 'var(--eh-bg-page)' }}>
          <Panel minSize={40} defaultSize={65} style={{ background: 'var(--eh-bg-page)' }}>
            <div className="overflow-auto p-4 h-100" style={{ background: 'var(--eh-bg-page)', overscrollBehaviorY: 'contain' }}>
          {loading ? (
            <div className="text-center py-5 text-muted">Loading programs…</div>
          ) : editingId === null ? (
            <div style={{ maxWidth: 860 }}>
              {apiError && <div className="alert alert-danger py-2 mb-3">{apiError}</div>}
              {programs.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem', opacity: 0.25 }}>&#8853;</div>
                  <p className="mb-3">No programs configured yet.</p>
                  <button className="btn btn-primary" onClick={startNew}>Add Program</button>
                </div>
              ) : (
                programs.map(p => (
                  <div key={p.id} className="card vbp-program-card" onClick={() => startEdit(p)}>
                    <div className="card-body d-flex align-items-center justify-content-between">
                      <div>
                        <div className="fw-bold" style={{ color: 'var(--eh-navy)' }}>
                          {p.name || <em className="text-muted fw-normal">Untitled</em>}
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.85rem' }}>{p.programType} &middot; {p.contractYear}</div>
                      </div>
                      <span style={{ color: 'var(--eh-teal)', fontSize: '1.3rem', lineHeight: 1 }}>&#8250;</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div style={{ maxWidth: 860 }}>
              <h5 style={{ color: 'var(--eh-navy)', fontWeight: 700, marginBottom: '1.5rem' }}>
                {editingId === 'new' ? 'New Program' : 'Edit Program'}
              </h5>

              {saved && (
                <div className="alert alert-success py-2 mb-3" role="alert">
                  &#10003; Program saved successfully.
                </div>
              )}
              {apiError && <div className="alert alert-danger py-2 mb-3">{apiError}</div>}

              <form onSubmit={handleSave}>
                <SectionHeading>Program Identity</SectionHeading>
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <Field label="Program Name">
                      <input className="form-control" value={draft.name}
                        onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
                        placeholder="e.g. 2026 MA Total Cost of Care" />
                    </Field>
                  </div>
                  <div className="col-md-3">
                    <Field label="Contract Year">
                      <select className="form-select" value={draft.contractYear}
                        onChange={e => setDraft(d => ({ ...d, contractYear: e.target.value }))}>
                        {['2024', '2025', '2026', '2027'].map(y => <option key={y}>{y}</option>)}
                      </select>
                    </Field>
                  </div>
                  <div className="col-md-3">
                    <Field label="Program Type">
                      <select className="form-select" value={draft.programType}
                        onChange={e => handleTypeChange(e.target.value)}>
                        <option value="">— Select type —</option>
                        {PROGRAM_TYPES.map(t => <option key={t}>{t}</option>)}
                      </select>
                    </Field>
                  </div>
                </div>

                {TypeFields
                  ? <TypeFields form={draft} setForm={setDraft} />
                  : <p className="text-muted" style={{ fontSize: '0.9rem' }}>Select a program type above to configure type-specific settings.</p>
                }

                <div className="mt-4 pt-3 border-top d-flex gap-2 align-items-center">
                  <button type="submit" className="btn btn-primary px-4 fw-bold"
                    disabled={!draft.programType || saving}>
                    {saving ? 'Saving…' : 'Save Program'}
                  </button>
                  <button type="button" className="btn btn-outline-secondary px-4" onClick={cancel} disabled={saving}>
                    Cancel
                  </button>
                  {editingId !== 'new' && !deleteConfirm && (
                    <button type="button" className="btn btn-outline-danger ms-auto"
                      onClick={() => setDeleteConfirm(true)}>
                      Delete Program
                    </button>
                  )}
                  {editingId !== 'new' && deleteConfirm && (
                    <div className="ms-auto d-flex align-items-center gap-2">
                      <span className="text-danger" style={{ fontSize: '0.9rem' }}>Delete this program?</span>
                      <button type="button" className="btn btn-danger btn-sm" onClick={handleDelete}>Yes, delete</button>
                      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setDeleteConfirm(false)}>Cancel</button>
                    </div>
                  )}
                </div>
              </form>
            </div>
          )}
            </div>
          </Panel>

          <PanelResizeHandle className="resize-handle">
            <div className="resize-handle-inner">
              <button
                className="collapse-btn"
                title={aiCollapsed ? 'Show AI assistant' : 'Hide AI assistant'}
                onClick={toggleAi}
                onPointerDown={e => e.stopPropagation()}
              >
                {aiCollapsed ? '◀' : '▶'}
              </button>
              <div className="resize-handle-bar" />
              <button
                className="collapse-btn"
                title={aiCollapsed ? 'Show AI assistant' : 'Hide AI assistant'}
                onClick={toggleAi}
                onPointerDown={e => e.stopPropagation()}
              >
                {aiCollapsed ? '◀' : '▶'}
              </button>
            </div>
          </PanelResizeHandle>

          <Panel
            ref={aiPanelRef}
            defaultSize={35}
            minSize={0}
            collapsible
            collapsedSize={0}
            onCollapse={() => setAiCollapsed(true)}
            onExpand={() => setAiCollapsed(false)}
          >
            <div className="h-100" style={{ background: 'var(--eh-bg-page)', borderLeft: '1px solid #dee2e6' }}>
              <ChatPanel activeTab="performance" chatStateRef={chatStateRef} />
            </div>
          </Panel>
        </PanelGroup>
      </div>
    </div>
  );
}
